import $ from "jquery"
import defer from "lodash/defer"
import { View } from "backbone"

// Represents a toggleable menu for search constraints & faceting.
//
// Expected markup:
//
//   <nav class="search-selector">
//     <button class="menu-button-contents" type="button">
//       Label: <span class="menu-button-text"></span>
//     </button>
//     <ul class="menu">
//       <li><input type="radio" ...><label ...>Option</label></li>
//     </ul>
//   </nav>

export default class SearchFacetMenuView extends View {
  initialize(options = {}) {
    this._toggleMenu = this._toggleMenu.bind(this)
    this._documentClick = this._documentClick.bind(this)
    this._selectOption = this._selectOption.bind(this)

    this.onSelect = options.onSelect
    this.$button = this.$el.find(".menu-button-contents")

    this._initSelectedOption()
    this._bindEvents()
  }

  // Public API used by search_page.js
  getSelectedLabel() {
    return this.selectedOption.find("label").text()
  }

  setMenuLabelText(text) {
    this.$el.find(".menu-button-text").text(text)
    this.$button.attr("aria-label", `${this.buttonLabel}: ${text}`)
  }

  restorePreviousOption() {
    this.selectedOption = this.previouslySelectedOption
    this._updateView()
  }

  selectedValue() {
    return this.selectedOption.find("input").prop("value")
  }

  // "Private"

  _initSelectedOption() {
    this.selectedOption = this.$el.find("li.checked")
    this.previouslySelectedOption = this.selectedOption
    this.buttonLabel = this.$button.attr("aria-label").split(":")[0]
    this._updateView()
    this.$button.attr("aria-expanded", "false")
  }

  _bindEvents() {
    this.$button.on("click", this._toggleMenu)

    // Track mouse interactions to prevent focusout closing the menu mid-click
    this.$el.on("mousedown", () => {
      this._mouseDownInside = true
    })
    $(document).on("mouseup", () => {
      this._mouseDownInside = false
    })

    this.$el.on("focusout", () => this._onFocusOut())
    this.$el.on("keydown", (e) => this._onKeyDown(e))

    this.$el.find("li label").on("keydown", (e) => this._onLabelKeyDown(e))
    this.$el.find("li").each((_i, el) => $(el).on("click", this._selectOption))
  }

  _onFocusOut() {
    defer(() => {
      const focusLeftNav = !this.$el[0].contains(document.activeElement)
      if (this._isOpen() && focusLeftNav && !this._mouseDownInside) {
        this._toggleMenu()
      }
    })
  }

  _onKeyDown(e) {
    if (!this._isOpen()) return

    switch (e.key) {
      case "Escape":
        e.preventDefault()
        this._toggleMenu()
        this.$button[0].focus()
        break

      case "Tab":
        if (e.shiftKey && this._focusedItemIndex() === 0) {
          e.preventDefault()
          this._toggleMenu()
          this.$button[0].focus()
        }
        break

      case "ArrowDown":
      case "ArrowUp":
        e.preventDefault()
        this._moveFocus(e.key === "ArrowDown" ? 1 : -1)
        break
    }
  }

  _onLabelKeyDown(e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      $(e.currentTarget).trigger("click")
    }
  }

  _selectOption(event) {
    this.previouslySelectedOption = this.selectedOption
    this.selectedOption = $(event.currentTarget)

    if (this.selectedOption.hasClass("checked")) {
      if (this.onSelect[this.selectedValue()]) {
        this.onSelect[this.selectedValue()]()
      }
    } else {
      this._updateView()
      if (this.onSelect[this.selectedValue()]) {
        this.onSelect[this.selectedValue()]()
      } else {
        this.onSelect.default()
      }
    }
  }

  _documentClick(event) {
    const isButtonClick = this.$button[0].contains(event.target)
    const isInsideNav = this.$el[0].contains(event.target)
    if (isInsideNav && !isButtonClick) return
    this._toggleMenu()
  }

  _toggleMenu(event) {
    event?.preventDefault()

    const opening = this.$el.toggleClass("open").hasClass("open")
    this.$button.attr("aria-expanded", String(opening))

    if (opening) {
      defer(() => {
        this.$button.off("click", this._toggleMenu)
        $(document).on("click", this._documentClick)
        const checked = this.$el.find("li.checked label")
        if (checked.length) checked[0].focus()
      })
    } else {
      defer(() => {
        this.$button.on("click", this._toggleMenu)
        $(document).off("click", this._documentClick)
      })
    }
  }

  _isOpen() {
    return this.$el.hasClass("open")
  }

  _labels() {
    return this.$el.find("li label")
  }

  _focusedItemIndex() {
    return this._labels().index(document.activeElement)
  }

  _moveFocus(delta) {
    const items = this._labels()
    const next = Math.min(Math.max(this._focusedItemIndex() + delta, 0), items.length - 1)
    items.eq(next)[0].focus()
  }

  _updateView() {
    const label = this.selectedOption.find("label").text()
    this.setMenuLabelText(label)
    this.$el.find("li").removeClass("checked")
    this.selectedOption.addClass("checked")
    this.selectedOption.find("input").prop("checked", true)
  }
}
